import { useState, useEffect } from 'react'
import { Table, Card, Row, Col, Button, Badge, Form, Spinner, Collapse } from 'react-bootstrap'
import { cdrApi, tenantsApi } from '../services/api'
import { useAuthStore } from '../store/authStore'

interface CDRRecord {
  id: string
  calldate: string
  src: string
  src_name: string | null
  src_type: string | null
  dst: string
  dst_name: string | null
  dst_type: string | null
  clid: string
  duration: number
  billsec: number
  disposition: string
  direction: string
  did: string
  trunk_name: string
  recording_file: string
  userfield: string
  linkedid: string
  lastapp: string
}

interface CallFlowEntry {
  calldate: string
  dst: string
  dst_name: string | null
  dst_type: string | null
  disposition: string
  lastapp: string
  duration: number
}

interface GroupedCall {
  linkedid: string
  calldate: string
  caller: string
  caller_name: string | null
  caller_clid: string
  final_outcome: string
  answered_by: string | null
  answered_by_name: string | null
  total_duration: number
  direction: string
  call_flow: CallFlowEntry[]
}

interface Tenant {
  id: string
  name: string
  domain: string
}

export default function CDR() {
  const { user, selectedTenant } = useAuthStore()
  const isSuperAdmin = user?.role === 'super_admin'

  const [calls, setCalls] = useState<CDRRecord[]>([])
  const [groupedCalls, setGroupedCalls] = useState<GroupedCall[]>([])
  const [stats, setStats] = useState({ total_calls: 0, answered_calls: 0, missed_calls: 0, average_duration_seconds: 0, answer_rate: 0, total_duration_seconds: 0 })
  const [loading, setLoading] = useState(true)
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [viewMode, setViewMode] = useState<'detailed' | 'grouped'>('grouped')
  const [expandedCalls, setExpandedCalls] = useState<Set<string>>(new Set())

  // Filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [filterTenantId, setFilterTenantId] = useState<string>('')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const pageSize = 50

  // Load tenants for super admin
  useEffect(() => {
    if (isSuperAdmin) {
      tenantsApi.list().then(res => setTenants(res.data.items || []))
    }
  }, [isSuperAdmin])

  // Set default tenant filter
  useEffect(() => {
    if (isSuperAdmin && selectedTenant) {
      setFilterTenantId(selectedTenant.id)
    }
  }, [isSuperAdmin, selectedTenant])

  // Load CDR data
  const loadData = async () => {
    setLoading(true)
    try {
      const params: Record<string, unknown> = {
        skip: page * pageSize,
        limit: pageSize
      }

      if (searchTerm) params.search = searchTerm
      if (startDate) params.start_date = new Date(startDate).toISOString()
      if (endDate) params.end_date = new Date(endDate + 'T23:59:59').toISOString()
      if (isSuperAdmin && filterTenantId) params.tenant_id = filterTenantId

      const statsParams: Record<string, unknown> = {}
      if (startDate) statsParams.start_date = new Date(startDate).toISOString()
      if (endDate) statsParams.end_date = new Date(endDate + 'T23:59:59').toISOString()
      if (isSuperAdmin && filterTenantId) statsParams.tenant_id = filterTenantId

      if (viewMode === 'grouped') {
        const [groupedRes, statsRes] = await Promise.all([
          cdrApi.grouped(params),
          cdrApi.stats(statsParams)
        ])
        setGroupedCalls(groupedRes.data.items || [])
        setTotal(groupedRes.data.total || 0)
        setStats(statsRes.data)
      } else {
        const [callsRes, statsRes] = await Promise.all([
          cdrApi.list(params),
          cdrApi.stats(statsParams)
        ])
        setCalls(callsRes.data.items || [])
        setTotal(callsRes.data.total || 0)
        setStats(statsRes.data)
      }
    } catch (error) {
      console.error('Error loading CDR:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [page, filterTenantId, viewMode])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(0)
    loadData()
  }

  const handleReset = () => {
    setSearchTerm('')
    setStartDate('')
    setEndDate('')
    setPage(0)
    loadData()
  }

  const toggleExpand = (linkedid: string) => {
    const newExpanded = new Set(expandedCalls)
    if (newExpanded.has(linkedid)) {
      newExpanded.delete(linkedid)
    } else {
      newExpanded.add(linkedid)
    }
    setExpandedCalls(newExpanded)
  }

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (mins < 60) return `${mins}m ${secs}s`
    const hours = Math.floor(mins / 60)
    return `${hours}h ${mins % 60}m`
  }

  const getDispositionBadge = (disposition: string) => {
    switch (disposition) {
      case 'ANSWERED': return <Badge bg="success">Angenommen</Badge>
      case 'BUSY': return <Badge bg="warning">Besetzt</Badge>
      case 'NO ANSWER': return <Badge bg="danger">Nicht angenommen</Badge>
      case 'FAILED': return <Badge bg="dark">Fehlgeschlagen</Badge>
      case 'CONGESTION': return <Badge bg="secondary">Überlastet</Badge>
      default: return <Badge bg="secondary">{disposition}</Badge>
    }
  }

  const getOutcomeBadge = (outcome: string) => {
    switch (outcome) {
      case 'answered': return <Badge bg="success">Angenommen</Badge>
      case 'missed': return <Badge bg="danger">Verpasst</Badge>
      case 'voicemail': return <Badge bg="info">Mailbox</Badge>
      default: return <Badge bg="secondary">{outcome}</Badge>
    }
  }

  const getDirectionIcon = (direction: string) => {
    switch (direction) {
      case 'inbound': return <i className="bi bi-telephone-inbound text-success me-1" title="Eingehend"></i>
      case 'outbound': return <i className="bi bi-telephone-outbound text-primary me-1" title="Ausgehend"></i>
      case 'internal': return <i className="bi bi-arrow-left-right text-secondary me-1" title="Intern"></i>
      default: return null
    }
  }

  const getDstTypeIcon = (dstType: string | null) => {
    switch (dstType) {
      case 'extension': return <i className="bi bi-telephone text-primary me-1"></i>
      case 'ring_group': return <i className="bi bi-people text-info me-1"></i>
      case 'voicemail': return <i className="bi bi-voicemail text-warning me-1"></i>
      case 'time_condition': return <i className="bi bi-clock text-secondary me-1"></i>
      case 'announcement': return <i className="bi bi-megaphone text-purple me-1"></i>
      case 'queue': return <i className="bi bi-list-ol text-info me-1"></i>
      case 'ivr': return <i className="bi bi-diagram-3 text-success me-1"></i>
      case 'feature': return <i className="bi bi-gear text-secondary me-1"></i>
      case 'external': return <i className="bi bi-globe text-muted me-1"></i>
      default: return null
    }
  }

  const formatDestination = (dst: string, dstName: string | null, dstType: string | null) => {
    return (
      <>
        {getDstTypeIcon(dstType)}
        <strong>{dstName || dst}</strong>
        {dstName && dstName !== dst && (
          <span className="small text-muted ms-1">({dst})</span>
        )}
      </>
    )
  }

  const formatCaller = (src: string, srcName: string | null, clid?: string) => {
    const displayName = srcName || (clid ? clid.replace(/<.*>/, '').replace(/"/g, '').trim() : null)
    return (
      <>
        <strong>{src}</strong>
        {displayName && displayName !== src && (
          <div className="small text-muted">{displayName}</div>
        )}
      </>
    )
  }

  const totalPages = Math.ceil(total / pageSize)

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          <i className="bi bi-telephone-history me-2"></i>
          Anrufhistorie (CDR)
        </h4>
        <div className="d-flex gap-2">
          <Button
            variant={viewMode === 'grouped' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => { setViewMode('grouped'); setPage(0) }}
          >
            <i className="bi bi-collection me-1"></i> Gruppiert
          </Button>
          <Button
            variant={viewMode === 'detailed' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => { setViewMode('detailed'); setPage(0) }}
          >
            <i className="bi bi-list-ul me-1"></i> Detailliert
          </Button>
          <Button variant="outline-secondary" size="sm">
            <i className="bi bi-download me-1"></i> Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <div className="h3 mb-0 text-primary">{stats.total_calls}</div>
              <small className="text-muted">Gesamt</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <div className="h3 mb-0 text-success">{stats.answered_calls}</div>
              <small className="text-muted">Angenommen</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <div className="h3 mb-0 text-danger">{stats.missed_calls}</div>
              <small className="text-muted">Verpasst</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <div className="h3 mb-0">{stats.answer_rate.toFixed(1)}%</div>
              <small className="text-muted">Annahmerate</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <div className="h3 mb-0">{formatDuration(Math.round(stats.average_duration_seconds))}</div>
              <small className="text-muted">Ø Dauer</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <div className="h3 mb-0">{formatDuration(stats.total_duration_seconds)}</div>
              <small className="text-muted">Gesamtdauer</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Search/Filter Form */}
      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleSearch}>
            <Row className="align-items-end">
              {isSuperAdmin && (
                <Col md={2}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Mandant</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filterTenantId}
                      onChange={(e) => setFilterTenantId(e.target.value)}
                    >
                      <option value="">Alle Mandanten</option>
                      {tenants.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
              <Col md={isSuperAdmin ? 4 : 6}>
                <Form.Group>
                  <Form.Label className="small mb-1">Suche (Name, Nummer, Caller-ID)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Name oder Nummer..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small mb-1">Von Datum</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small mb-1">Bis Datum</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <div className="d-flex gap-2">
                  <Button type="submit" variant="primary" size="sm">
                    <i className="bi bi-search me-1"></i> Suchen
                  </Button>
                  <Button type="button" variant="outline-secondary" size="sm" onClick={handleReset}>
                    <i className="bi bi-x-lg"></i>
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* CDR Table */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>
            <i className="bi bi-list me-2"></i>
            {viewMode === 'grouped' ? 'Anrufe (gruppiert)' : 'Anrufliste (detailliert)'}
            {total > 0 && <Badge bg="secondary" className="ms-2">{total}</Badge>}
          </span>
          {totalPages > 1 && (
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                <i className="bi bi-chevron-left"></i>
              </Button>
              <span className="small">Seite {page + 1} von {totalPages}</span>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                <i className="bi bi-chevron-right"></i>
              </Button>
            </div>
          )}
        </Card.Header>

        {viewMode === 'grouped' ? (
          /* Grouped View */
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th style={{width: '30px'}}></th>
                <th style={{width: '140px'}}>Zeit</th>
                <th>Anrufer</th>
                <th>Ziel / Verlauf</th>
                <th style={{width: '80px'}}>Dauer</th>
                <th style={{width: '120px'}}>Ergebnis</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Laden...
                  </td>
                </tr>
              ) : groupedCalls.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-muted">
                    <i className="bi bi-inbox me-2"></i>
                    Keine Anrufe gefunden
                  </td>
                </tr>
              ) : (
                groupedCalls.map(c => (
                  <>
                    <tr key={c.linkedid} className="align-middle">
                      <td>
                        {c.call_flow.length > 1 && (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={() => toggleExpand(c.linkedid)}
                          >
                            <i className={`bi bi-chevron-${expandedCalls.has(c.linkedid) ? 'down' : 'right'}`}></i>
                          </Button>
                        )}
                      </td>
                      <td className="small">
                        {new Date(c.calldate).toLocaleString('de-CH', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td>
                        {getDirectionIcon(c.direction)}
                        {formatCaller(c.caller, c.caller_name, c.caller_clid)}
                      </td>
                      <td>
                        {c.call_flow.length === 1 ? (
                          formatDestination(c.call_flow[0].dst, c.call_flow[0].dst_name, c.call_flow[0].dst_type)
                        ) : (
                          <>
                            {formatDestination(c.call_flow[0].dst, c.call_flow[0].dst_name, c.call_flow[0].dst_type)}
                            {c.answered_by && (
                              <span className="ms-2">
                                <i className="bi bi-arrow-right text-muted mx-1"></i>
                                <i className="bi bi-telephone text-success me-1"></i>
                                <strong>{c.answered_by_name || c.answered_by}</strong>
                              </span>
                            )}
                            <Badge bg="light" text="dark" className="ms-2">{c.call_flow.length} Schritte</Badge>
                          </>
                        )}
                      </td>
                      <td>{formatDuration(c.total_duration)}</td>
                      <td>{getOutcomeBadge(c.final_outcome)}</td>
                    </tr>
                    {c.call_flow.length > 1 && (
                      <tr key={`${c.linkedid}-flow`}>
                        <td colSpan={6} className="p-0">
                          <Collapse in={expandedCalls.has(c.linkedid)}>
                            <div>
                              <Table size="sm" className="mb-0 bg-light">
                                <tbody>
                                  {c.call_flow.map((flow, idx) => (
                                    <tr key={idx} className="small">
                                      <td style={{width: '30px'}}></td>
                                      <td style={{width: '140px'}} className="text-muted">
                                        {new Date(flow.calldate).toLocaleTimeString('de-CH', {
                                          hour: '2-digit',
                                          minute: '2-digit',
                                          second: '2-digit'
                                        })}
                                      </td>
                                      <td>
                                        <i className="bi bi-arrow-return-right text-muted me-2"></i>
                                        {formatDestination(flow.dst, flow.dst_name, flow.dst_type)}
                                      </td>
                                      <td className="text-muted">{flow.lastapp}</td>
                                      <td style={{width: '80px'}}>{flow.duration > 0 ? formatDuration(flow.duration) : '-'}</td>
                                      <td style={{width: '120px'}}>{getDispositionBadge(flow.disposition)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </Collapse>
                        </td>
                      </tr>
                    )}
                  </>
                ))
              )}
            </tbody>
          </Table>
        ) : (
          /* Detailed View */
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th style={{width: '160px'}}>Datum/Zeit</th>
                <th>Richtung</th>
                <th>Von</th>
                <th>Nach</th>
                <th style={{width: '80px'}}>Dauer</th>
                <th style={{width: '120px'}}>Status</th>
                <th>Trunk/DID</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Laden...
                  </td>
                </tr>
              ) : calls.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-4 text-muted">
                    <i className="bi bi-inbox me-2"></i>
                    Keine Anrufe gefunden
                  </td>
                </tr>
              ) : (
                calls.map(c => (
                  <tr key={c.id}>
                    <td className="small">
                      {new Date(c.calldate).toLocaleString('de-CH', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td>
                      {getDirectionIcon(c.direction)}
                      <span className="small text-muted">
                        {c.direction === 'inbound' ? 'Ein' : c.direction === 'outbound' ? 'Aus' : 'Intern'}
                      </span>
                    </td>
                    <td>
                      {formatCaller(c.src, c.src_name, c.clid)}
                    </td>
                    <td>
                      {formatDestination(c.dst, c.dst_name, c.dst_type)}
                    </td>
                    <td>{formatDuration(c.billsec)}</td>
                    <td>{getDispositionBadge(c.disposition)}</td>
                    <td className="small text-muted">
                      {c.trunk_name && <span className="me-2">{c.trunk_name}</span>}
                      {c.did && <Badge bg="light" text="dark">{c.did}</Badge>}
                      {c.recording_file && (
                        <Button variant="link" size="sm" className="p-0 ms-2" title="Aufnahme anhören">
                          <i className="bi bi-play-circle"></i>
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}

        {totalPages > 1 && (
          <Card.Footer className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Zeige {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} von {total} Einträgen
            </small>
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                <i className="bi bi-chevron-left me-1"></i> Zurück
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                Weiter <i className="bi bi-chevron-right ms-1"></i>
              </Button>
            </div>
          </Card.Footer>
        )}
      </Card>
    </div>
  )
}
